// OPEN A PAGE ON THE BENEFICIARY A LINK NAMES (ZEN-697).
//
// The "Review now" button in the approval email names one beneficiary —
// "Bella submitted receipt(s) for your review" — so the page it opens should be
// on Bella, not on the whole list. The link carries `?student=<id>`, the same
// word the Dashboard's own links already use.
//
// ★ THE LINK MOVES THE APP-WIDE PICKER, it does not filter one page quietly.
// Every screen reads whose work to show from the picker at the top of the app,
// so a page that disagreed with it would show Bella under a picker reading
// "All beneficiaries". (Recommended to the account owner 27 Sep 2026 as the
// answer to ZEN-697's open question; see the pull request for her ruling.)
//
// ★ AN ID NOBODY IN THIS HOUSEHOLD HAS IS IGNORED, NOT TRIED. The id is only
// honoured once it has been found in this household's own beneficiary list —
// the list the picker itself offers. Anything else (a mistyped link, a link
// forwarded from another household, a beneficiary since deleted) changes
// nothing: no request goes out carrying it, no name appears, and the page
// opens exactly as it would have without the link.
//
// ★ THE ADDRESS IS CLEARED ONCE READ, so a refresh, or choosing someone else
// in the picker afterwards, is not pulled back to the link's beneficiary.
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const STUDENT_PARAM = "student";

/**
 * Reads a beneficiary from the address, switches the picker to them if they
 * belong to this household, then clears it from the address.
 *
 * Returns `settling`: true while a named beneficiary is still being checked,
 * so the page can hold its list back rather than flash everyone's work first.
 *
 * `alsoClear` names other address words the page has already read and wants
 * gone at the same moment. The address is rewritten in ONE place so two
 * clean-ups cannot race and put back what the other removed (the Dashboard's
 * `year`).
 */
export function useStudentFromAddress({ alsoClear = [] } = {}) {
  const { selectedStudentId, selectStudent } = useAuth();
  const [params, setParams] = useSearchParams();
  const named = params.get(STUDENT_PARAM);
  // The same list, under the same key, that the picker and the page already
  // read — so this costs no extra request.
  const { data: students, isSuccess, isError, isFetchedAfterMount } = useQuery({
    queryKey: ["students"],
    queryFn: async () => (await api.get("/students")).data,
    enabled: !!named,
    // Asked again on arrival even when a recent copy is remembered: without
    // it, a link followed inside the app (the Overview's Review now) found
    // the list still fresh, never fetched, and waited for ever. (ZEN-768)
    refetchOnMount: "always",
  });
  // ★ ONLY A LIST FETCHED SINCE THIS PAGE OPENED IS TRUSTED. A list already
  // remembered from earlier in the session can be out of date — somebody
  // removed since, or another sign-in's list — and checking the link against
  // it could send an id this household no longer has. Waiting for the fresh
  // answer costs one request the page was making anyway. (Adversarial review,
  // round 2.)
  const answered = isFetchedAfterMount && (isSuccess || isError);

  // ★ THE ADDRESS IS CLEARED ONLY ONCE THE PICKER REALLY READS THE NAMED
  // PERSON, not straight after asking it to. The picker tidies itself when the
  // same list arrives: if it remembered somebody since removed, it resets to
  // "All beneficiaries" — and depending on which of the two runs last, that
  // reset could land on top of this choice and undo it. Asking again until
  // the picker agrees makes the order irrelevant. It cannot loop: the tidy-up
  // only ever clears a person who is NOT on the list, and this only ever
  // chooses one who is. (Adversarial review, round 1.)
  useEffect(() => {
    if (!named || !answered) return;
    const known = isSuccess && (students || []).some((s) => s.id === named);
    if (known && selectedStudentId !== named) {
      selectStudent(named);
      return;
    }
    const rest = new URLSearchParams(params);
    rest.delete(STUDENT_PARAM);
    alsoClear.forEach((word) => rest.delete(word));
    setParams(rest, { replace: true });
  }, [named, answered, isSuccess, selectedStudentId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { settling: !!named };
}
